import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { BrandingTarget } from "types/landlord/address";
import { getTargetName, propertiesLabel } from "./helpers";

export const OverwriteBrandingDialog = ({
  opened,
  onClose,
  onConfirm,
  sourceName,
  selectedCount,
  affectedTargets,
  isPending,
  canClose
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sourceName: string;
  selectedCount: number;
  affectedTargets: BrandingTarget[];
  isPending: boolean;
  canClose: boolean;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      panelClassName="max-w-[720px] w-full"
    >
      <header className="flex items-start justify-between gap-4 border-b border-alpha-40 px-6 py-6">
        <h3 className="text-xl font-bold text-content-1000">
          Overwrite existing branding?
        </h3>
        <IconButton
          HugeIcon={Cancel01Icon}
          disabled={!canClose}
          onClick={onClose}
          size={IconButtonSize.BASE}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </header>

      <div className="border-b border-alpha-40 px-6 py-4">
        <p className="rounded-lg bg-warning-50 p-4 text-sm text-warning-800">
          <span className="font-bold">
            {affectedTargets.length} of the {selectedCount}{" "}
            {propertiesLabel(selectedCount, dictionary)}
          </span>{" "}
          you selected already have their own branding set. Applying will
          overwrite their existing logo, color, landlord name, phone, and email
          with the values from {sourceName}.
        </p>
      </div>

      <div className="max-h-[240px] overflow-y-auto border-b border-alpha-40 px-6 py-4">
        {affectedTargets.map((target) => (
          <div
            className="flex items-center justify-between gap-4 py-2"
            key={target.id}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-content-700">
                {getTargetName(target, dictionary)}
              </p>
              <p className="truncate text-sm text-content-300">
                {target.address || "No address"}
              </p>
            </div>
            <p className="shrink-0 text-xs font-bold text-warning-700">
              Current branding will be replaced
            </p>
          </div>
        ))}
      </div>

      <footer className="flex items-center justify-end gap-2 px-6 py-4">
        <Button disabled={!canClose} onClick={onClose}>
          Cancel
        </Button>
        <Button
          loading={isPending}
          onClick={onConfirm}
          variant={ButtonVariant.PRIMARY}
        >
          Overwrite and apply
        </Button>
      </footer>
    </Modal>
  );
};
